import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from './AuthContext';

export const WishlistContext = createContext();

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (_error) {
    return {};
  }
};

export function WishlistProvider({ children }) {
  const { user, token } = useContext(AuthContext);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchWishlist = useCallback(async () => {
    if (!user || !token) {
      setWishlistItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/wishlist', {
        headers: { 'x-auth-token': token },
      });
      const data = await readJson(response);
      if (!response.ok) throw new Error(data.msg || 'Unable to load your wishlist.');
      setWishlistItems(Array.isArray(data) ? data : []);
    } catch (wishlistError) {
      console.error('Failed to fetch wishlist:', wishlistError);
      setWishlistItems([]);
      setError(wishlistError.message || 'Unable to load your wishlist.');
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const isWishlisted = useCallback((productId) => (
    wishlistItems.some((item) => Number(item.id || item.product_id) === Number(productId))
  ), [wishlistItems]);

  const toggleWishlist = async (product) => {
    if (!user || !token) return { requiresAuth: true };

    const productId = Number(product?.id);
    if (!Number.isInteger(productId) || productId <= 0) {
      throw new Error('This product cannot be saved right now.');
    }

    const currentlySaved = isWishlisted(productId);
    const previousItems = wishlistItems;
    setError('');
    setWishlistItems((current) => currentlySaved
      ? current.filter((item) => Number(item.id || item.product_id) !== productId)
      : [product, ...current.filter((item) => Number(item.id || item.product_id) !== productId)]);

    try {
      const response = currentlySaved
        ? await fetch(`/api/wishlist/${productId}`, {
          method: 'DELETE',
          headers: { 'x-auth-token': token },
        })
        : await fetch('/api/wishlist', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token,
          },
          body: JSON.stringify({ productId }),
        });
      const data = await readJson(response);
      if (!response.ok) throw new Error(data.msg || 'Unable to update your wishlist.');
      return { saved: !currentlySaved };
    } catch (wishlistError) {
      setWishlistItems(previousItems);
      setError(wishlistError.message || 'Unable to update your wishlist.');
      throw wishlistError;
    }
  };

  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      loading,
      error,
      isWishlisted,
      toggleWishlist,
      refreshWishlist: fetchWishlist,
    }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  return useContext(WishlistContext);
}
